import {
  StyledCard,
  StyledInput
} from '@bo-components/common/StyledComponents';
import { useEffect, useMemo } from 'react';
import { ControlledSelect } from '@library/components/selects';
import { DEFAULT_PLAYER_DATA } from '@bo-constants/players';
import { FormActionButtons } from '@library/components/buttons';
import type { LeaguesList } from '@library/types/leagues';
import type { PlayerData } from '@library/types/player';
import PlayerPlaceholder from '@library/assets/images/icons/player-placeholder.svg';
import type { SelectOptions } from '@library/types/utils';
import StyledModal from '@bo-components/common/StyledModal';
import { formatDate } from '@library/component-utils/dateTime';
import { selectHasEditPlayers } from '@bo-selectors/utilsDashes';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import useUtilDetailsModal from '@bo-hooks/utilsDashboards/useUtilDetailsModal';

export interface PlayerDetailsModalProps {
  isNewPlayer: boolean;
  isOpen: boolean;
  leaguesList: LeaguesList;
  onClose: () => void;
  onSubmit: (formData: Partial<PlayerData>) => void;
  playerData: PlayerData;
  submitMessage: string;
}

export const PlayerDetailsModal = ({
  isNewPlayer,
  isOpen,
  leaguesList,
  onClose,
  onSubmit,
  playerData,
  submitMessage
}: PlayerDetailsModalProps) => {
  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<PlayerData>({
    mode: 'onChange'
  });

  const {
    formData,
    handleCancel,
    isReadOnly,
    setFormData,
    setIsReadOnly,
    onClickEdit,
    onClickClose
  } = useUtilDetailsModal({
    isNewItem: isNewPlayer,
    defaultItemData: DEFAULT_PLAYER_DATA,
    modalData: playerData,
    onClose,
    reset
  });

  const hasCreateOrEditPlayers = useSelector(selectHasEditPlayers);

  const isEditMode = !isReadOnly || isNewPlayer;

  const options: SelectOptions = useMemo(() => {
    return leaguesList.map((league) => {
      const { icon, id, imageUrl, name } = league;

      const leagueIcon = (
        <img
          alt={icon}
          className="order-none mr-2 size-6 flex-none grow-0"
          src={imageUrl}
        />
      );

      return {
        key: id,
        icon: leagueIcon,
        name,
        value: id
      };
    });
  }, [leaguesList]);

  useEffect(() => {
    if (isNewPlayer) {
      setIsReadOnly(false);
    }
  }, [isNewPlayer, setIsReadOnly]);

  useEffect(() => {
    if (playerData) {
      setFormData(playerData);
    }
  }, [playerData, setFormData]);

  useEffect(() => {
    reset(formData);
  }, [reset, formData]);

  return (
    <StyledModal
      isEditAllowed={hasCreateOrEditPlayers}
      isOpen={isOpen}
      onClose={onClickClose}
      onEdit={isNewPlayer ? undefined : onClickEdit}
      testId="player-details-modal"
      bigTitle={formData.name ?? 'Player Name'}
      smallTitle={isEditMode ? 'Edit Player' : 'View Player'}
    >
      <form>
        <div className="flex gap-6">
          <StyledCard title="General Information">
            <img
              src={formData.imageUrl || PlayerPlaceholder}
              alt="Player"
              className="border-soFresh-140 h-auto w-32 rounded-md border"
            />
            <StyledInput
              error={errors.name?.message}
              label="Name"
              name="name"
              readOnly={isReadOnly}
              register={register}
              required={isEditMode}
            />
            <div className="flex gap-4">
              <StyledInput
                error={errors.position?.message}
                label="Position"
                name="position"
                readOnly={isReadOnly}
                register={register}
                required={isEditMode}
              />
              <StyledInput
                error={errors.team?.message}
                label="Team"
                name="team"
                readOnly={isReadOnly}
                register={register}
                required={isEditMode}
              />
              <StyledInput
                error={errors.leagueName?.message}
                label="League"
                name="leagueName"
                readOnly={isReadOnly}
                register={register}
                required={isEditMode}
              >
                <ControlledSelect
                  allOption={false}
                  control={control}
                  disabled={!isEditMode}
                  label="leagues"
                  name="leagueId"
                  options={options}
                  rules={{ required: 'League is required' }}
                  size="small"
                  testId="league-dropdown"
                />
              </StyledInput>
            </div>
            <StyledInput
              label="Image URL"
              name="imageUrl"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Old League"
              name="oldLeague"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Team Name"
              name="teamName"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Display Name"
              name="displayName"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Created At"
              name="createdAt"
              readOnly
              register={register}
              value={formatDate({ date: formData.createdAt })}
            />
            <StyledInput
              label="Updated At"
              name="updatedAt"
              readOnly
              register={register}
              value={formatDate({ date: formData.updatedAt })}
            />
          </StyledCard>

          <StyledCard title="Scoring">
            <StyledInput
              label="Swish"
              name="swishId"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Fantasy data"
              name="fantasyDataId"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Sportsradar"
              name="sportsradarId"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Market"
              name="market"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Combo"
              name="combo"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Oddsjam"
              name="oddsjamId"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Team Data"
              name="teamData"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Stat Averages"
              name="statAverages"
              readOnly={isReadOnly}
              register={register}
            />
          </StyledCard>
        </div>
      </form>

      {isEditMode && (
        <div className="border-soFresh-140 bg-soFresh-100 sticky bottom-0 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
          {submitMessage ? (
            <p>{submitMessage}</p>
          ) : (
            <FormActionButtons
              className="flex items-center justify-end gap-4 border-none py-3 pr-5"
              primary={{
                onClick: handleSubmit(onSubmit),
                disabled: !isValid
              }}
              secondary={{ onClick: handleCancel }}
            />
          )}
        </div>
      )}
    </StyledModal>
  );
};
